<template>
	<div class="user-promocode-stats">
		<h3 class="stats-page-title">3KK promokoodit</h3>
		<div v-if="loading" class="stats-state">Ladataan käyttäjäalennuskoodien tilastoja...</div>
		<div v-else-if="loadError" class="stats-state stats-state-error">
			Käyttäjäalennuskoodien tilastoja ei voitu ladata.
		</div>
		<div v-else>
			<div class="stats-summary-cards">
				<div class="stats-card">
					<span class="stats-card-label">Yhteensä rivejä</span>
					<strong class="stats-card-value">{{ totals.rows_count }}</strong>
				</div>
				<div class="stats-card">
					<span class="stats-card-label">Yhteensä tuotetta ostettu</span>
					<strong class="stats-card-value">{{ totals.used_count }}</strong>
				</div>
			</div>

			<div class="stats-chart-wrapper" v-if="chartData.labels.length">
				<Bar
					:chart-id="chartId"
					:chart-data="chartData"
					:chart-options="chartOptions"
					:dataset-id-key="datasetIdKey"
					:plugins="plugins"
					:css-classes="cssClasses"
					:styles="styles"
					:height="380"
				/>
			</div>

			<div class="stats-table-wrapper">
				<table class="stats-table">
					<thead>
						<tr>
							<th>Päivämäärä</th>
							<th>Rivimäärä</th>
							<th>Ostettu</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="row in summaryRows" :key="row.date">
							<td>{{ row.date }}</td>
							<td>{{ row.rows_count }}</td>
							<td>{{ row.used_count }}</td>
						</tr>
					</tbody>
					<tfoot>
						<tr>
							<th>Yhteensä</th>
							<th>{{ totals.rows_count }}</th>
							<th>{{ totals.used_count }}</th>
						</tr>
					</tfoot>
				</table>
			</div>
		</div>
	</div>
</template>

<script>
import { Bar } from "vue-chartjs/legacy";
import {
	Chart as ChartJS,
	Title,
	Tooltip,
	Legend,
	BarElement,
	CategoryScale,
	LinearScale,
} from "chart.js";

ChartJS.register(
	Title,
	Tooltip,
	Legend,
	BarElement,
	CategoryScale,
	LinearScale
);

export default {
	name: "UserPromocodeStats",
	components: {
		Bar,
	},
	props: {
		chartId: {
			type: String,
			default: "user-promocode-stats-chart",
		},
		datasetIdKey: {
			type: String,
			default: "label",
		},
		cssClasses: {
			type: String,
			default: "",
		},
		styles: {
			type: Object,
			default: () => ({}),
		},
		plugins: {
			type: Object,
			default: () => ({}),
		},
	},
	data() {
		return {
			loading: false,
			loadError: false,
			statsPath: "/1.0/front/promocodes/summary",
			rowsCount: 0,
			usedCount: 0,
			summaryRows: [],
			chartData: {
				labels: [],
				datasets: [
					{
						label: "Rivimäärä",
						backgroundColor: "#1e88e5",
						borderRadius: 4,
						data: [],
					},
					{
						label: "Ostettu",
						backgroundColor: "#43a047",
						borderRadius: 4,
						data: [],
					},
				],
			},
			chartOptions: {
				responsive: true,
				maintainAspectRatio: false,
				interaction: {
					mode: "index",
					intersect: false,
				},
				plugins: {
					legend: {
						position: "top",
					},
					title: {
						display: true,
						text: "3KK promokoodit",
					},
				},
				scales: {
					x: {
						ticks: {
							maxRotation: 45,
							minRotation: 45,
						},
					},
					y: {
						beginAtZero: true,
					},
				},
			},
		};
	},
	computed: {
		totals() {
			return {
				rows_count:
					this.rowsCount ||
					this.summaryRows.reduce((sum, row) => sum + row.rows_count, 0),
				used_count:
					this.usedCount ||
					this.summaryRows.reduce((sum, row) => sum + row.used_count, 0),
			};
		},
	},
	mounted() {
		this.getStats();
	},
	methods: {
		fillChart() {
			this.chartData.labels = this.summaryRows.map((row) => row.date);
			this.chartData.datasets[0].data = this.summaryRows.map((row) => row.rows_count);
			this.chartData.datasets[1].data = this.summaryRows.map((row) => row.used_count);
		},
		getStats() {
			this.loading = true;
			this.loadError = false;

			this.$http
				.get(this.statsPath)
				.then((response) => {
					if (response.status == 200) {
						this.rowsCount = response.data.rows_count || 0;
						this.usedCount = response.data.used_count || 0;
						this.summaryRows = Array.isArray(response.data.summary)
							? response.data.summary
							: [];
						this.fillChart();
					} else if (response.status == 401) {
						this.$root.$emit("session_failed");
					} else {
						this.loadError = true;
						this.$notify.error({
							message: "Jokin vika tapahtui käyttäjäalennuskoodien tilastoja haettaessa",
							position: "top right",
							timeOut: this.$store.state.notifytimeoutmedium,
						});
					}
				})
				.catch((error) => {
					window.__logAxiosErrorIfDebug(error, {
						context: "UserPromocodeStats.vue - getStats",
					});
					this.loadError = true;
					this.$notify.error({
						message: "Jokin vika tapahtui käyttäjäalennuskoodien tilastoja haettaessa",
						position: "top right",
						timeOut: this.$store.state.notifytimeoutmedium,
					});
				})
				.finally(() => {
					this.loading = false;
				});
		},
	},
};
</script>

<style scoped>
.user-promocode-stats {
	display: flex;
	flex-direction: column;
	gap: 24px;
}

.stats-state {
	padding: 16px;
	border-radius: 8px;
	background: #f5f7fa;
	color: #334155;
}

.stats-page-title {
	margin: 0;
	color: #0f172a;
}

.stats-state-error {
	background: #fef2f2;
	color: #b91c1c;
}

.stats-summary-cards {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
	gap: 16px;
}

.stats-card {
	padding: 18px 20px;
	border: 1px solid #dbe3ec;
	border-radius: 12px;
	background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
}

.stats-card-label {
	display: block;
	margin-bottom: 8px;
	color: #64748b;
	font-size: 14px;
}

.stats-card-value {
	color: #0f172a;
	font-size: 28px;
}

.stats-chart-wrapper {
	min-height: 380px;
	padding: 16px;
	border: 1px solid #dbe3ec;
	border-radius: 12px;
	background: #ffffff;
}

.stats-table-wrapper {
	overflow-x: auto;
	border: 1px solid #dbe3ec;
	border-radius: 12px;
	background: #ffffff;
}

.stats-table {
	width: 100%;
	border-collapse: collapse;
}

.stats-table th,
.stats-table td {
	padding: 12px 16px;
	border-bottom: 1px solid #e5e7eb;
	text-align: left;
}

.stats-table thead th,
.stats-table tfoot th {
	background: #f8fafc;
	color: #0f172a;
}

.stats-table tbody tr:nth-child(even) {
	background: #f8fbff;
}

.stats-table tfoot tr th {
	border-bottom: 0;
}
</style>
